/**
 * Document-throughput classify, manual-fact, OCR, and review-outcome actions.
 */
import { formatInteger } from "./format.js";
import { labelize } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import { leadFactorySourceDocumentReviewDispositionUrl } from "./dashboard-urls.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import type { SourceDocumentReviewFocus } from "./dashboard-lead-factory-types.js";
import {
  leadFactoryDocumentClasses,
  leadFactoryManualFactFields,
  leadFactoryManualFactValueTypes,
  leadFactoryReviewOutcomes,
  type LeadFactoryManualFactField
} from "./dashboard-lead-factory-constants.js";

export function bindDocumentThroughputOutputActions(
  statusElement: HTMLElement,
  actions: DashboardActionHost,
  showReview: (sourceDocumentId: string, statusElement: HTMLElement, focus?: SourceDocumentReviewFocus) => Promise<void>
): void {
  document.querySelectorAll<HTMLFormElement>("[data-document-throughput-classification-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const sourceDocumentId = form.dataset.sourceDocumentId;
      const documentClass = form.querySelector<HTMLSelectElement>("[data-document-throughput-classification-class]")?.value ?? "";
      if (sourceDocumentId) void submitDocumentThroughputClassification(sourceDocumentId, documentClass, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-document-throughput-manual-fact-form]").forEach((form) => {
    const fieldSelect = form.querySelector<HTMLSelectElement>("[data-document-throughput-manual-fact-field]");
    if (fieldSelect) {
      applyDocumentThroughputManualFactInputType(form, fieldSelect.value);
      fieldSelect.addEventListener("change", () => applyDocumentThroughputManualFactInputType(form, fieldSelect.value));
    }
    form.querySelector<HTMLButtonElement>("[data-document-throughput-manual-fact-page-focus]")?.addEventListener("click", () => {
      const sourceDocumentId = form.dataset.sourceDocumentId;
      const fieldName = fieldSelect?.value ?? "";
      const sourcePage = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-source-page]")?.value ?? "";
      const evidenceExcerpt = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-evidence-excerpt]")?.value ?? "";
      if (!sourceDocumentId) return;
      const page = Number.parseInt(sourcePage.trim(), 10);
      if (!Number.isFinite(page) || page < 1) {
        statusElement.textContent = "Enter a source page before opening the PDF review workspace.";
        return;
      }
      statusElement.textContent = `${sourceDocumentId}: opening PDF review at page ${formatInteger(page)}...`;
      void showReview(sourceDocumentId, statusElement, {
        page,
        fieldName,
        evidenceExcerpt: evidenceExcerpt.trim()
      });
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const sourceDocumentId = form.dataset.sourceDocumentId;
      const fieldName = fieldSelect?.value ?? "";
      const value = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-value]")?.value ?? "";
      const normalizedValue = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-normalized]")?.value ?? "";
      const sourcePage = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-source-page]")?.value ?? "";
      const evidenceExcerpt = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-evidence-excerpt]")?.value ?? "";
      const context = form.dataset.manualFactContext || "document_bottleneck_queue";
      if (sourceDocumentId) void submitDocumentThroughputManualFact(sourceDocumentId, fieldName, value, normalizedValue, sourcePage, evidenceExcerpt, statusElement, context, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-document-throughput-ocr-result-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const sourceDocumentId = form.dataset.sourceDocumentId;
      const text = form.querySelector<HTMLTextAreaElement>("[data-document-throughput-ocr-result-text]")?.value ?? "";
      if (sourceDocumentId) void submitDocumentThroughputOcrResult(sourceDocumentId, text, statusElement, actions);
    });
  });
  document.querySelectorAll<HTMLFormElement>("[data-document-throughput-review-outcome-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const sourceDocumentId = form.dataset.sourceDocumentId;
      const taskId = form.dataset.taskId;
      const outcome = form.querySelector<HTMLSelectElement>("[data-document-throughput-review-outcome]")?.value ?? "";
      const reason = form.querySelector<HTMLInputElement>("[data-document-throughput-review-reason]")?.value ?? "";
      const confirmed = form.querySelector<HTMLInputElement>("[data-document-throughput-review-confirm]")?.checked ?? false;
      if (sourceDocumentId && taskId) void submitDocumentThroughputReviewOutcome(sourceDocumentId, taskId, outcome, reason, confirmed, statusElement, actions);
    });
  });
}

export function applyDocumentThroughputManualFactInputType(form: HTMLFormElement, fieldName: string): void {
  const field = leadFactoryManualFactFields.includes(fieldName as LeadFactoryManualFactField) ? fieldName as LeadFactoryManualFactField : "owner_name";
  const valueType = leadFactoryManualFactValueTypes[field];
  const valueInput = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-value]");
  const normalizedInput = form.querySelector<HTMLInputElement>("[data-document-throughput-manual-fact-normalized]");
  if (!valueInput || !normalizedInput) return;
  valueInput.type = valueType === "date" ? "date" : valueType === "number" ? "number" : "text";
  valueInput.inputMode = valueType === "money" || valueType === "number" ? "decimal" : "text";
  valueInput.placeholder = valueType === "money" ? "Amount from PDF" : valueType === "date" ? "YYYY-MM-DD" : "Value from PDF";
  normalizedInput.type = valueType === "date" ? "date" : valueType === "money" || valueType === "number" ? "number" : "text";
  normalizedInput.inputMode = valueType === "money" || valueType === "number" ? "decimal" : "text";
  normalizedInput.placeholder = valueType === "money" ? "Numeric amount" : valueType === "date" ? "YYYY-MM-DD" : "Normalized";
}

export async function submitDocumentThroughputClassification(sourceDocumentId: string, documentClass: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  if (!leadFactoryDocumentClasses.includes(documentClass as (typeof leadFactoryDocumentClasses)[number])) {
    statusElement.textContent = `${documentClass} is not a known document class.`;
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: saving classification...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/classify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        documentClass,
        evidence: "Operator classified from Document Bottleneck Queue.",
        note: "Operator classified source document from Document Bottleneck Queue."
      })
    });
    const body = (await response.json()) as { error?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: classified as ${labelize(documentClass)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Document classification failed.";
  }
}

export async function submitDocumentThroughputManualFact(
  sourceDocumentId: string,
  fieldName: string,
  valueValue: string,
  normalizedValueValue: string,
  sourcePageValue: string,
  evidenceExcerptValue: string,
  statusElement: HTMLElement,
  context = "document_bottleneck_queue",
  actions: DashboardActionHost
): Promise<void> {
  const value = valueValue.trim();
  const normalizedValue = normalizedValueValue.trim() || value;
  const sourcePage = sourcePageValue.trim();
  const evidenceExcerpt = evidenceExcerptValue.trim();
  if (!leadFactoryManualFactFields.includes(fieldName as LeadFactoryManualFactField)) {
    statusElement.textContent = `${fieldName || "blank"} is not a known fact field.`;
    return;
  }
  if (!value) {
    statusElement.textContent = "Fact capture cancelled; no value entered.";
    return;
  }
  if (!/^\d+$/.test(sourcePage) || Number.parseInt(sourcePage, 10) < 1) {
    statusElement.textContent = "Fact capture cancelled; source page is required.";
    return;
  }
  if (!evidenceExcerpt) {
    statusElement.textContent = "Fact capture cancelled; evidence excerpt is required.";
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: capturing ${labelize(fieldName)}...`;
  const sourceLabel =
    context === "lead_supply_priority"
      ? "Lead Supply Priority Work"
      : context === "mortgage_evidence_review"
        ? "Mortgage Evidence Review"
        : "Document Bottleneck Queue";
  const provenance =
    context === "lead_supply_priority"
      ? "operator_lead_supply_priority"
      : context === "mortgage_evidence_review"
        ? "operator_mortgage_evidence_review"
        : "operator_document_bottleneck_queue";
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/source-documents/${encodeURIComponent(sourceDocumentId)}/facts`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        fieldName,
        value,
        normalizedValue,
        sourcePage: Number.parseInt(sourcePage, 10),
        evidenceExcerpt,
        confidence: 1,
        provenance,
        note: `Operator captured ${fieldName} from ${sourceLabel}.`
      })
    });
    const body = (await response.json()) as {
      error?: string;
      fieldName?: string;
      leadStatus?: string | null;
      contactReadiness?: string | null;
      resolvedBlockingReasons?: string[];
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const resolved = (body.resolvedBlockingReasons ?? []).map(labelize).join(", ");
    const readiness = body.contactReadiness ? `; readiness ${labelize(body.contactReadiness)}` : "";
    const leadStatus = body.leadStatus ? `; lead ${labelize(body.leadStatus)}` : "";
    const resolvedText = resolved ? `; resolved ${resolved}` : "";
    statusElement.textContent = `${sourceDocumentId}: captured ${labelize(body.fieldName ?? fieldName)}${resolvedText}${readiness}${leadStatus}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Fact capture failed.";
  }
}

export async function submitSourceDocumentReviewDisposition(sourceDocumentId: string, outcome: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const normalizedOutcome = outcome.trim();
  const evidenceExcerpt = window.prompt(
    "Evidence that the reviewed source has no street/property address:",
    "OCR reviewed: source contains legal/parcel context, but no street/property address."
  )?.trim();
  if (!evidenceExcerpt) {
    statusElement.textContent = "Source review disposition cancelled; evidence excerpt is required.";
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: routing source review disposition...`;
  try {
    const response = await authenticatedFetch(leadFactorySourceDocumentReviewDispositionUrl(sourceDocumentId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        outcome: normalizedOutcome,
        evidenceExcerpt,
        note: "Operator reviewed source document and routed property address gap from Lead Supply."
      })
    });
    const body = (await response.json()) as {
      error?: string;
      contactReadiness?: string | null;
      researchTaskId?: string;
      completedTaskIds?: string[];
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const completed = body.completedTaskIds?.length ? `; completed ${body.completedTaskIds.length} stale task(s)` : "";
    const readiness = body.contactReadiness ? `; readiness ${labelize(body.contactReadiness)}` : "";
    statusElement.textContent = `${sourceDocumentId}: routed to property-address research ${body.researchTaskId ?? ""}${readiness}${completed}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Source review disposition failed.";
  }
}

export async function submitDocumentThroughputOcrResult(sourceDocumentId: string, textValue: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const text = textValue.trim();
  if (!text) {
    statusElement.textContent = "OCR ingestion cancelled; no OCR text entered.";
    return;
  }
  statusElement.textContent = `${sourceDocumentId}: ingesting OCR text...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/ingest-ocr-result", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        text,
        ocrEngine: "dashboard_manual_ocr",
        confidence: 0.9
      })
    });
    const body = (await response.json()) as { error?: string; factsInserted?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: ingested OCR text and inserted ${formatInteger(body.factsInserted ?? 0)} facts. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "OCR ingestion failed.";
  }
}

export async function submitDocumentThroughputReviewOutcome(
  sourceDocumentId: string,
  taskId: string,
  outcome: string,
  reasonValue: string,
  confirmed: boolean,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  if (!leadFactoryReviewOutcomes.includes(outcome as (typeof leadFactoryReviewOutcomes)[number])) {
    statusElement.textContent = `Review outcome cancelled; choose one of ${leadFactoryReviewOutcomes.join(", ")}.`;
    return;
  }
  if (!confirmed) {
    statusElement.textContent = "Review outcome cancelled; confirm before recording.";
    return;
  }
  const reason = reasonValue.trim() || outcome;
  statusElement.textContent = `${sourceDocumentId}: recording ${labelize(outcome)}...`;
  try {
    const response = await authenticatedFetch("/dashboard/lead-factory/review-queue/complete", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        taskIds: [taskId],
        outcome,
        reason,
        note: `Document Bottleneck Queue review decision for ${sourceDocumentId}: ${outcome}.`
      })
    });
    const body = (await response.json()) as { error?: string; completedCount?: number; skippedCount?: number; outcome?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: recorded ${body.outcome ?? outcome}; completed ${formatInteger(body.completedCount ?? 0)}, skipped ${formatInteger(body.skippedCount ?? 0)}. Refreshing...`;
    window.setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Review outcome failed.";
  }
}

